import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

export interface SearchableSelectOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-searchable-select',
  standalone: false,
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.scss'
})
export class SearchableSelectComponent implements OnChanges {
  @Input() id = '';
  @Input() value = '';
  @Input() options: SearchableSelectOption[] = [];
  @Input() placeholder = 'Select';
  @Input() searchPlaceholder = 'Search...';
  @Input() disabled = false;
  @Input() showPlaceholderWhenDisabledAndEmpty = false;
  @Input() dropdownMaxHeight = 220;
  @Input() allowCustomValue = false;

  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  searchTerm = '';
  filteredOptions: SearchableSelectOption[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.applyFilter();
    }

    if (changes['disabled'] && this.disabled) {
      this.closeDropdown();
    }
  }

  get displayLabel(): string {
    if (
      this.showPlaceholderWhenDisabledAndEmpty &&
      this.disabled &&
      !String(this.value || '').trim()
    ) {
      return this.placeholder;
    }

    const selected = this.options.find((item) => String(item.id || '') === String(this.value || ''));
    if (selected?.name) {
      return selected.name;
    }

    if (this.allowCustomValue && String(this.value || '').trim()) {
      return String(this.value || '').trim();
    }

    return this.placeholder;
  }

  get hasSearchTerm(): boolean {
    return !!this.searchTerm.trim();
  }

  get hasValue(): boolean {
    return !!String(this.value || '').trim();
  }

  get trimmedSearchTerm(): string {
    return String(this.searchTerm || '').trim();
  }

  get hasCustomOption(): boolean {
    return this.allowCustomValue && !!this.trimmedSearchTerm && this.filteredOptions.length === 0;
  }

  get normalizedDropdownMaxHeight(): string {
    const height = Number(this.dropdownMaxHeight);
    return Number.isFinite(height) && height > 0 ? `${height}px` : '220px';
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    if (this.isOpen) {
      this.closeDropdown(true);
      return;
    }

    this.isOpen = true;
    this.searchTerm = '';
    this.applyFilter();
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  onSearchInput(event: Event): void {
    if (this.disabled) return;
    const target = event.target as HTMLInputElement | null;
    this.searchTerm = String(target?.value || '');
    this.applyFilter();
  }

  onClearSearch(event: MouseEvent): void {
    if (this.disabled) return;
    event.stopPropagation();
    this.searchTerm = '';
    this.applyFilter();
    this.searchInput?.nativeElement.focus();
  }

  onClearValue(event: MouseEvent): void {
    if (this.disabled || !this.hasValue) return;
    event.stopPropagation();
    this.valueChange.emit('');
  }

  onSearchEnter(event: Event): void {
    if (this.disabled) return;
    event.preventDefault();

    if (this.hasCustomOption) {
      this.commitCustomValue();
      this.closeDropdown();
      return;
    }

    if (this.filteredOptions.length === 1) {
      this.onSelect(this.filteredOptions[0]);
    }
  }

  onSelect(option: SearchableSelectOption): void {
    if (this.disabled) return;
    const nextValue = String(option.id || '');
    this.valueChange.emit(nextValue);
    this.closeDropdown();
  }

  onSelectCustomValue(): void {
    if (this.disabled || !this.hasCustomOption) return;
    this.commitCustomValue();
    this.closeDropdown();
  }

  isSelected(option: SearchableSelectOption): boolean {
    return String(option.id || '') === String(this.value || '');
  }

  trackByOptionId(index: number, item: SearchableSelectOption): string {
    return `${item.id}-${index}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    if (!target) return;
    if (!this.host.nativeElement.contains(target)) {
      this.closeDropdown(true);
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }

  private closeDropdown(commitCustomValue = false): void {
    if (commitCustomValue) {
      this.commitCustomValue();
    }
    this.isOpen = false;
    this.searchTerm = '';
    this.applyFilter();
  }

  private commitCustomValue(): void {
    if (!this.hasCustomOption) {
      return;
    }

    this.valueChange.emit(this.trimmedSearchTerm);
  }

  private applyFilter(): void {
    const normalizedTerm = this.normalize(this.searchTerm);
    if (!normalizedTerm) {
      this.filteredOptions = [...this.options];
      return;
    }

    this.filteredOptions = this.options.filter((item) => {
      const label = this.normalize(item.name);
      const id = this.normalize(item.id);
      return label.includes(normalizedTerm) || id.includes(normalizedTerm);
    });
  }

  private normalize(value: string | number | null | undefined): string {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  constructor(private host: ElementRef<HTMLElement>) {
    this.applyFilter();
  }
}
